// 📄 components/StepPOMapping.tsx
import React from 'react';
import { HelpCircle } from 'lucide-react';
import { PurchaseOrderFormData } from '../../../../../services/commerce/integrationService';

interface StepPOMappingProps {
  cigProjectId: string;
  customHeaderFields: boolean;
  customItemFields: boolean;
  onChange: (field: keyof PurchaseOrderFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepPOMapping({ cigProjectId, customHeaderFields, customItemFields, onChange, isInvalid }: StepPOMappingProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      {/* SPRO Route */}
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Portal de Integración / SAP Managed Gateway</span>
        <p className="font-mono text-xs text-gray-300">
          <span>SAP Ariba CIG portal → Managed Gateway → My Projects → Custom Mapping Manager</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-blue-400">4. CIG Custom Mapping & Mapeos Adicionales</h3>
        <p className="text-sm text-gray-400">
          Establece el identificador de tu proyecto en SAP Integration Suite, Managed Gateway (CIG) y configura si tu integración requiere mapeos de campos Z o campos de extensión personalizados.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 space-y-4">
        <div className="max-w-md">
          <label className="block text-xs font-medium text-gray-400 mb-1">
            ID del Proyecto CIG (Project ID) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={cigProjectId}
            onChange={(e) => onChange('cigProjectId', e.target.value.toUpperCase())}
            placeholder="ej. PRJ-123456"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500 ${
              isInvalid && !cigProjectId ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>

        <div className="flex flex-col space-y-2 pt-2 border-t border-white/5">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Campos Personalizados (Z-Fields)</span>
          
          <label className="flex items-center gap-3 bg-zinc-900/40 p-3 rounded-lg border border-white/5 cursor-pointer hover:bg-white/10 transition-all">
            <input
              type="checkbox"
              checked={customHeaderFields}
              onChange={(e) => onChange('customHeaderFields', e.target.checked)}
              className="rounded bg-zinc-800 border-white/10 text-blue-500 focus:ring-0"
            />
            <div>
              <span className="text-sm font-medium block">Habilitar Mapeo de Campos de Cabecera Personalizados</span>
              <span className="text-xs text-gray-400">Permite mapear campos Z en el nodo de cabecera de la Orden de Compra (ej. ZTERM, etc.)</span>
            </div>
          </label>

          <label className="flex items-center gap-3 bg-zinc-900/40 p-3 rounded-lg border border-white/5 cursor-pointer hover:bg-white/10 transition-all">
            <input
              type="checkbox"
              checked={customItemFields}
              onChange={(e) => onChange('customItemFields', e.target.checked)}
              className="rounded bg-zinc-800 border-white/10 text-blue-500 focus:ring-0"
            />
            <div>
              <span className="text-sm font-medium block">Habilitar Mapeo de Campos de Posición Personalizados</span>
              <span className="text-xs text-gray-400">Permite mapear campos Z en el nivel de posición de compras (ej. Centro Coste Z, etc.)</span>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-yellow-400 flex gap-3 text-sm">
        <HelpCircle className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Mapeos Personalizados y XSLT</p>
          <p className="text-xs text-yellow-500/80 mt-0.5">
            Si habilita los campos personalizados, recuerde cargar su esquema WSDL o XML con los campos extendidos en el portal de CIG para que los archivos XSLT de conversión de IDoc a cXML no generen errores de schema validation.
          </p>
        </div>
      </div>
    </div>
  );
}
